import React from 'react';
import { useEffect, useState, useCallback } from 'react';
import { Button, Card, Spinner, Alert } from '@/components/ui';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import { DeviceSelector } from '@/components/shared/DeviceSelectorNew';
import useGlobalDeviceSelector from '@/hooks/useGlobalDeviceSelector';
import { getHistoryError, resetError } from '@/services/api/deviceHistoryService';
import type { DeviceMessageItem } from '@/types/api';
import { setLoading } from '@/store/slices/appSlice';
import { useDispatch } from 'react-redux';


const HistoryPage: React.FC = () => {
  const dispatch = useDispatch();
  const { selectedDeviceId, onDeviceChange } = useGlobalDeviceSelector();
  const [items, setItems] = useState<DeviceMessageItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  const [pendingIndex, setPendingIndex] = useState<number | null>(null);

  const fetchHistory = useCallback(async () => {
    if (!selectedDeviceId) return;
    dispatch(setLoading(true));
    setError(null);
    try {
      const data = await getHistoryError(selectedDeviceId);
      setItems(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load history');
      console.error('Failed to load history:', e);
    } finally {
      dispatch(setLoading(false));
    }
  }, [selectedDeviceId]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleItemClick = (idx: number, item: DeviceMessageItem) => {
    if (item.is_read) return; // Only reset unread errors
    setPendingIndex(idx);
    setConfirmOpen(true);
  };

  const handleResetConfirm = async () => {
    if (pendingIndex == null || !selectedDeviceId) return;
    dispatch(setLoading(true));
    try {
      const updated = await resetError(selectedDeviceId, pendingIndex);
      setItems(Array.isArray(updated) ? updated : []);
    } catch (e: any) {
      setError(e?.message || 'Failed to reset error');
      console.error('Failed to reset error:', e);
    } finally {
      dispatch(setLoading(false));
      setConfirmOpen(false);
      setPendingIndex(null);
    }
  };

  const renderList = () => {
    if (!items.length) {
      return (
        <div className="p-6 text-center text-gray-500 dark:text-gray-400">No history messages.</div>
      );
    }

    return (
      <div className="divide-y divide-gray-200 dark:divide-gray-700">
        {items.map((item, idx) => (
          <div key={idx}>
            {item.show_category && (
              <div className="bg-gray-50 dark:bg-gray-800 px-4 py-2 text-xs font-semibold text-gray-600 dark:text-gray-300">
                {item.subcategory_date}
              </div>
            )}
            <button
              className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-gray-50 dark:hover:bg-gray-800 ${item.is_read ? '' : 'bg-yellow-50/40 dark:bg-yellow-900/20'}`}
              onClick={() => handleItemClick(idx, item)}
            >
              <div className="flex flex-col flex-1 min-w-0">
                <div className="text-sm text-gray-900 dark:text-gray-100 truncate">{item.msg_body}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{item.time}</div>
              </div>
              {!item.is_read && (
                <span className="inline-flex items-center text-[10px] font-medium text-yellow-700 dark:text-yellow-300 bg-yellow-100 dark:bg-yellow-900/40 px-2 py-0.5 rounded">UNREAD</span>
              )}
            </button>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-4 text-gray-900 dark:text-gray-100">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-semibold tracking-tight dark:text-gray-100">History</h2>
        </div>
        <div className="flex items-center">
          <DeviceSelector onDeviceChange={onDeviceChange} selectedDeviceId={selectedDeviceId || ''} />
        </div>
      </div>

      {/* Main Card */}
      <Card>
        <div className="p-4">
          {/* Actions */}
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm text-gray-500 dark:text-gray-400">Device: {selectedDeviceId || '-'}</div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="twoTone" onClick={fetchHistory} disabled={!selectedDeviceId}>
                Refresh
              </Button>
            </div>
          </div>

          {/* Content */}
          {error && (
            <Alert className="mb-3" type="danger" showIcon>
              {error}
            </Alert>
          )}

          {renderList()}
        </div>
      </Card>

      {/* Confirm Reset Dialog */}
      <ConfirmDialog
        open={confirmOpen}
        title="Reset Error?"
        onCancel={() => {
          setConfirmOpen(false);
          setPendingIndex(null);
        }}
        onOk={handleResetConfirm}
        disableOk={pendingIndex == null}
      >
        <div className="text-sm text-gray-700 dark:text-gray-300">This will mark the selected error as read. Continue?</div>
      </ConfirmDialog>
    </div>
  );
};

export default HistoryPage;